import { useQuery } from '@tanstack/react-query';
import { integrations } from '@cwos/api-client';
import type { integrations as IntegrationsNS } from '@cwos/api-client';
import { connectorAvailability, importMayBeBehind } from './settings/integrationsLogic.js';

/**
 * dataHealth.ts — the ONE cross-check shared by every screen that claims
 * "nothing is being missed" (Needs Attention's trust sentence, Insights'
 * "nothing is waiting on a first look").
 *
 * THE DEFECT THIS CLOSES. Both claims were computed purely from THEIR OWN
 * queue being empty — a count of alerts, a count of findings — with zero
 * awareness that a connected integration could be sitting on an incomplete
 * historical import or an unhealthy sync. `domains/alerts` and
 * `domains/rules` had never once referenced `domains/integrations` in either
 * direction (verified), so a genuinely honest zero on the QUEUE could still
 * sit beside a screen-wide claim of full trust that the data behind it did
 * not support.
 *
 * WHAT THIS IS NOT. It does not replace the bounded alert signals in
 * `apps/api/src/domains/alerts/data-health.ts` — those raise a real queue row
 * for a condition serious enough to need disposing of (stale, failed,
 * withheld). This is the QUIETER, WIDER check: even a historical import that
 * is calmly still running (no alert, nothing wrong) means the screen's
 * figures do not yet cover full history, and "you can trust it" is a claim
 * about completeness, not merely about the absence of alarm. So this reads
 * `historicalSyncComplete` directly rather than waiting for it to become
 * alert-worthy.
 *
 * REUSED, NOT RECOMPUTED. Every fact below is a field `getConnectorsOverview`
 * (apps/api/.../integrations/service.ts) already projects and the Integration
 * Center card already renders — `connectorAvailability` is the same
 * predicate `overallBanner` uses to keep a withheld connector from driving a
 * claim it cannot back with an action.
 */

export interface DataHealthGap {
  /** One sentence, ready to append to an otherwise-unconditional claim. */
  message: string;
}

/**
 * THE THREE-VALUED ANSWER THIS CROSS-CHECK OWES ITS CALLERS.
 *
 * "Is anything wrong?" has three honest answers — yes, no, and we don't know —
 * and `DataHealthGap | null` only had room for two. `null` was made to stand
 * for BOTH "checked, and it is clear" and "did not check" (loading, a
 * transient failure, or a reader who cannot hold `integrations:read` at all),
 * and a caller cannot tell those apart from the value alone. Every one of
 * Needs Attention's and Right Now's trust sentences is a claim of the first
 * kind ("you can trust it — we never hide anything"), and rendering it from
 * the second is the false reassurance PH-ATT-001 is about: a query that has
 * not answered is not evidence of health.
 *
 *   'clear'   — the read came back and named no gap. The only kind that may
 *               license an unqualified trust sentence.
 *   'gap'     — the read came back and named one; `message` is `dataHealthGap`'s
 *               single, worst-first caveat.
 *   'unknown' — the read has not answered: still loading, it failed, or the
 *               caller was never asked (no `integrations:read`). This is NOT a
 *               synonym for 'clear' and must never render as one — a caller
 *               that cannot tell 'unknown' from 'clear' has reintroduced the
 *               defect this type exists to close.
 */
export type DataHealthCheck =
  { kind: 'clear' } | { kind: 'gap'; message: string } | { kind: 'unknown' };

/**
 * The sentence a caller appends to its own empty-queue statement when the
 * cross-check answered 'unknown' — the queue may be empty, but this did not
 * verify data health, so the claim must not be read as completeness.
 */
export const DATA_HEALTH_UNKNOWN_CAVEAT =
  'we could not check data health just now, so do not read this as everything being current.';

/**
 * The worst-first single caveat, or `null` when nothing here contradicts a
 * full-trust claim. Returns at most ONE gap — the point is a short, honest
 * caveat next to an empty state, not a second alert queue.
 */
export function dataHealthGap(
  connectors: readonly IntegrationsNS.ConnectorOverview[],
): DataHealthGap | null {
  // A withheld connector cannot be acted on (every write door 409s —
  // overallBanner's own reasoning in integrationsLogic.ts), so it is excluded
  // here for the same reason: naming it beside a "nothing is being missed"
  // claim would point at a remedy that does not exist.
  const instances = connectors
    .filter((c) => connectorAvailability(c).kind === 'available')
    .map((c) => ({ name: c.displayName, instance: c.instance }))
    .filter(
      (x): x is { name: string; instance: IntegrationsNS.ConnectorInstance } => x.instance !== null,
    );

  // Worse first: a connector already needing attention says more than one
  // whose history has simply not finished importing yet.
  // The CONNECTION's trouble, not open Needs Attention items (CORE-AUD-220).
  const attention = instances.find((x) => importMayBeBehind(x.instance));
  if (attention) {
    return {
      message:
        `${attention.name} needs attention in the Integration Center — its figures may be ` +
        'behind until that is resolved.',
    };
  }

  const incomplete = instances.find((x) => !x.instance.historicalSyncComplete);
  if (incomplete) {
    return {
      message:
        `Historical import for ${incomplete.name} has not completed yet, so its figures ` +
        'cover only what has synced so far.',
    };
  }

  return null;
}

/**
 * The one caveat sentence a caller appends to its own "queue is empty"
 * statement — or `undefined` when the check came back 'clear' and the
 * caller's default, unqualified trust copy may stand.
 *
 * Shared so `Attention.tsx`, `RightNow.tsx` and `insightsLogic.ts` cannot
 * drift on what "the queue is empty, but…" means for a 'gap' versus an
 * 'unknown' — the whole point of PH-ATT-001/PH-ATT-002 is that both are
 * non-answers a screen must render honestly, not that each screen invents its
 * own wording for one of them.
 */
export function dataHealthCaveat(check: DataHealthCheck): string | undefined {
  if (check.kind === 'clear') return undefined;
  return check.kind === 'gap' ? check.message : DATA_HEALTH_UNKNOWN_CAVEAT;
}

/**
 * The shared read behind `dataHealthGap` — and, since PH-ATT-001, the ONLY
 * function any screen may use to decide whether it may render an unqualified
 * trust sentence. Every caller must switch on `.kind` and treat 'unknown'
 * exactly like 'gap' for the purpose of the trust CLAUSES (never render them),
 * even though the two produce different caveat text.
 *
 * `enabled` is the caller's own `mayReadIntegrationsHealth(has)` — the query
 * is never fired for a reader who cannot hold the key, matching this
 * codebase's rule for `alerts.list` in Attention.tsx: an optional read that
 * would only ever 403 is not fired at all. But "never fired" is itself an
 * unanswered question, not a healthy one, so it resolves to 'unknown' — the
 * same as a query in flight or one that failed — rather than to 'clear'.
 *
 * THIS USED TO FAIL OPEN. Before PH-ATT-001, every one of these non-answers
 * (loading, a transient failure, `enabled: false`) collapsed to a bare `null`
 * that a caller could not distinguish from "checked and healthy", and the
 * module said so in so many words. A sentence whose second clause is "we
 * never hide anything" may only be rendered from evidence that this read
 * actually came back clear.
 */
export function useDataHealthCheck(enabled: boolean): DataHealthCheck {
  const query = useQuery({
    queryKey: ['integrations', 'connectors', 'data-health-gap'],
    queryFn: () => integrations.connectors(),
    enabled,
    staleTime: 60_000,
  });
  // `query.data === undefined` is the loading case AND the disabled case:
  // `enabled: false` never fetches, so `data` never arrives — there is no
  // separate branch to write for "never asked", it falls out of the same test.
  if (query.isError || query.data === undefined) return { kind: 'unknown' };
  const gap = dataHealthGap(query.data);
  return gap ? { kind: 'gap', message: gap.message } : { kind: 'clear' };
}
